import React, { useEffect, useRef, useState } from 'react';
import { Box, Button } from '@heycater/design-system';
import { ButtonBase, Hidden } from '@material-ui/core';
import { AccountCircle } from '@material-ui/icons';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CloseIcon from '@material-ui/icons/Close';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { cssVars } from 'lib/cssVariables';
import { useBreakpointDown } from 'qualification/hooks/useBreakpoints';
import useCurrentUser from 'qualification/hooks/useCurrentUser';
import { CONTACT_PHONE, LINKS } from 'shared/constants';
import { CUSTOMER_APP_HOST } from 'shared/helpers/urls';
import useAuthentication from 'shared/hooks/useAuthentication';
import { useElementSize } from 'shared/hooks/useElementSize';
import {
  formatRating,
  formatRatingCount,
  getRating,
  getRatingCount,
} from 'shared/services/trustedShops/handleRatings';
import { Ratings } from 'shared/services/trustedShops/model';
import Logo from 'shared/svg/Logo';
import MenuIcon from 'shared/svg/MenuIcon';
import TrustedShopsLogo from 'shared/svg/TrustedShopsLogo';
import TrustedShopsStars from 'shared/svg/TrustedShopsStars';
import { buildFunnelUrl } from 'static_pages/marketing_pages/helpers';
import Container from 'system/components/Container';

type LinkData = {
  href: string;
  outsideLink?: boolean;
  label: string;
};

type MainLink = LinkData & {
  children?: LinkData[];
};

interface Props {
  hideNavigationLinks?: boolean;
  renderCTA?: React.ReactNode;
  ratings?: Ratings;
  mainLinks?: MainLink[];
}

const LOCALE_OPTIONS = [
  { label: 'DE', locale: 'de' },
  { label: 'EN', locale: 'en' },
  { label: 'EN (UK)', locale: 'en-uk' },
];

function getLocaleLabel(locale: string | undefined): string {
  if (!locale) return 'EN';
  if (locale === 'en-uk') return 'EN (UK)';
  if (locale.startsWith('de')) return 'DE';
  return 'EN';
}

function Navigation({
  hideNavigationLinks,
  renderCTA,
  ratings,
  mainLinks,
}: Props) {
  const { t } = useTranslation('static_pages');
  const [visibleDropdown, setVisibleDropdown] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobile = useBreakpointDown('sm');
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onToggleDropdownVisibility = React.useCallback(
    (name: string) => {
      if (name === visibleDropdown) {
        setVisibleDropdown(null);
      } else {
        setVisibleDropdown(name);
      }
    },
    [visibleDropdown]
  );

  const onMouseEnterDropdown = React.useCallback((name: string) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setVisibleDropdown(name);
  }, []);

  const onMouseLeaveDropdown = React.useCallback(() => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setVisibleDropdown(null);
    }, 150);
  }, []);

  const { ref: navRef, height: navHeight } = useElementSize();
  const router = useRouter();

  useEffect(() => {
    cssVars.topBarHeight.setValue(`${Math.floor(navHeight)}px`);
    return () => cssVars.topBarHeight.setValue(0);
  }, [navHeight]);

  const { currentUserAccount, loading } = useCurrentUser();
  const { logout } = useAuthentication();
  const isLoggedIn = !loading && currentUserAccount;

  const topLinks: MainLink[] = mainLinks || [
    {
      href: '',
      label: t('common:navigation.services'),
      children: [
        {
          href: '/heykantine',
          label: t('common:navigation.dailyOfficeMeals'),
        },
        {
          href: '/meeting-event-catering',
          label: t('common:navigation.meetingsCatering'),
        },
        {
          href: '/events',
          label: t('common:navigation.fullServiceEvents'),
        },
        {
          href: '/pantry',
          label: t('common:navigation.officeDrinksSnack'),
        },
      ],
    },
    {
      href: '',
      label: t('common:navigation.about'),
      children: [
        {
          href: '/about-us',
          label: t('common:navigation.aboutUs'),
        },
        {
          href: '/nachhaltigkeit',
          label: t('common:navigation.sustainability'),
        },
        {
          href: '/hub',
          label: t('common:navigation.blog'),
        },
      ],
    },
    {
      href: '',
      label: t('common:navigation.forPartners'),
      children: [
        {
          href: '/partner-page',
          label: t('common:navigation.becomePartner'),
        },
      ],
    },
    {
      href: '',
      label: t('common:navigation.more'),
      children: [
        {
          href: LINKS.career,
          label: t('common:navigation.career'),
          outsideLink: true,
        },
        {
          href: `${CUSTOMER_APP_HOST}/account/login`,
          label: t('common:navigation.signIn'),
          outsideLink: true,
        },
        {
          href: '/impressum',
          label: t('common:navigation.imprint'),
        },
        {
          href: '/presse',
          label: t('common:navigation.press'),
        },
        {
          href: '/contact-01',
          label: t('common:navigation.contact'),
        },
      ],
    },
  ];

  const userMenuLinks = (() => {
    let menuList = [];
    if (currentUserAccount?.roles?.includes('HEYKANTINE_CONSUMER')) {
      menuList.push({
        href: `${CUSTOMER_APP_HOST}/kantine/home`,
        label: t('common:navigation.heykantine'),
      });
      return menuList;
    }

    menuList = [
      {
        href: buildFunnelUrl({ router }),
        label: t('common:labels.requestCatering'),
      },
      {
        href: `${CUSTOMER_APP_HOST}/account/requests`,
        label: t('common:navigation.events'),
      },
      {
        href: `${CUSTOMER_APP_HOST}/account`,
        label: t('common:navigation.account'),
      },
    ];

    if (currentUserAccount?.owner?.heykantineEnabled) {
      menuList.push({
        href: `${CUSTOMER_APP_HOST}/account/kantine`,
        label: t('common:navigation.heykantine'),
      });
    }

    return menuList;
  })();

  return (
    <NavWrapper
      ref={navRef}
      className="main-navigation"
      style={{
        minHeight: 'inherit',
      }}
    >
      <NavbarEyebrow>
        <Container as="header">
          <NavbarEyebrowFlexWrapper>
            {mobile ? (
              <></>
            ) : (
              <TrustedShopsWrapper>
                <Stars>
                  <TrustedShopsStars />
                </Stars>
                {ratings && (
                  <>
                    <Rating>
                      <strong>{formatRating(getRating(ratings))}</strong>
                      <RatingText>
                        &nbsp;{t('trustedShops.ratingText')}
                      </RatingText>
                    </Rating>
                    <RatingCount>
                      {t('trustedShops.ratingCount', {
                        amount: formatRatingCount(getRatingCount(ratings)),
                      })}
                    </RatingCount>
                  </>
                )}
                <TSLogoBox display="flex" alignItems="center">
                  <TrustedShopsLogo />
                </TSLogoBox>
              </TrustedShopsWrapper>
            )}
            <PhoneLink href={CONTACT_PHONE.href}>
              <strong>{t('common:navigation.questions')}</strong>
              {CONTACT_PHONE.formatted}
            </PhoneLink>
          </NavbarEyebrowFlexWrapper>
        </Container>
      </NavbarEyebrow>
      <Navbar>
        <Container>
          <NavbarWrapper>
            <NavbarLeft>
              <NavbarLogo
                href="https://www.heycater.com"
                aria-label="Home"
                id="navbar-logo"
              >
                <Logo />
              </NavbarLogo>
            </NavbarLeft>

            <NavbarCenter>
              <MobileMenuOverlay open={mobileMenuOpen}>
                {!hideNavigationLinks && (
                  <MenuList>
                    {topLinks.map((link) => (
                      <React.Fragment key={link.label}>
                        <MenuListItem
                          data-dropdown={!!link.children}
                          onMouseEnter={
                            !mobile && link.children
                              ? () => onMouseEnterDropdown(link.label)
                              : undefined
                          }
                          onMouseLeave={
                            !mobile && link.children
                              ? onMouseLeaveDropdown
                              : undefined
                          }
                        >
                          {link.children ? (
                            <MenuListItemLink
                              as="button"
                              aria-expanded={visibleDropdown === link.label}
                              onClick={() =>
                                onToggleDropdownVisibility(link.label)
                              }
                            >
                              {link.label}
                              {mobile &&
                                (visibleDropdown === link.label ? (
                                  <KeyboardArrowUpIcon fontSize="small" />
                                ) : (
                                  <KeyboardArrowDownIcon fontSize="small" />
                                ))}
                            </MenuListItemLink>
                          ) : (
                            <Link href={link.href} passHref>
                              <MenuListItemLink>{link.label}</MenuListItemLink>
                            </Link>
                          )}

                          {link.children && (
                            <DropdownContent
                              visible={visibleDropdown === link.label}
                              aria-expanded={visibleDropdown === link.label}
                              className="navigation-dropdown"
                            >
                              <DropdownMenu>
                                {link.children.map((child) =>
                                  child.outsideLink ? (
                                    <DropdownMenuLink
                                      key={child.href}
                                      href={child.href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <span>{child.label}</span>
                                      <ArrowForwardIcon fontSize="small" />
                                    </DropdownMenuLink>
                                  ) : (
                                    <Link
                                      key={child.href}
                                      href={child.href}
                                      passHref
                                    >
                                      <DropdownMenuLink
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <span>{child.label}</span>
                                        <ArrowForwardIcon fontSize="small" />
                                      </DropdownMenuLink>
                                    </Link>
                                  )
                                )}
                              </DropdownMenu>
                            </DropdownContent>
                          )}
                        </MenuListItem>
                      </React.Fragment>
                    ))}
                  </MenuList>
                )}

                {/* Right items inside mobile overlay only */}
                <MobileOnlyRightSection>
                  <MenuList>
                    <LocaleMenuListItem data-dropdown="true">
                      <LocaleButton
                        as="button"
                        aria-expanded={visibleDropdown === 'locale'}
                        onClick={() => onToggleDropdownVisibility('locale')}
                      >
                        {getLocaleLabel(router.locale)}
                        <KeyboardArrowDownIcon fontSize="small" />
                      </LocaleButton>
                      <LocaleDropdownContent
                        visible={visibleDropdown === 'locale'}
                        aria-expanded={visibleDropdown === 'locale'}
                        className="navigation-dropdown"
                      >
                        {LOCALE_OPTIONS.map(({ label, locale }) => (
                          <Link
                            key={locale}
                            href={router.asPath}
                            locale={locale}
                            passHref
                          >
                            <LocaleOption selected={router.locale === locale}>
                              {label}
                            </LocaleOption>
                          </Link>
                        ))}
                      </LocaleDropdownContent>
                    </LocaleMenuListItem>

                    {!isLoggedIn && !loading && (
                      <MenuListItemRight>
                        <LoginLink href={`${CUSTOMER_APP_HOST}/account/login`}>
                          {t('common:navigation.signIn')}
                        </LoginLink>
                      </MenuListItemRight>
                    )}
                    <MenuListItemRight wrapsButton>
                      {typeof renderCTA === 'function' ? (
                        renderCTA()
                      ) : (
                        <Link href={buildFunnelUrl({ router })} passHref>
                          <CTAButton
                            component="a"
                            rel="noopener"
                            id="navbar-find-caterers-mobile"
                          >
                            {t('common:navigation.requestNow')}
                          </CTAButton>
                        </Link>
                      )}
                    </MenuListItemRight>
                  </MenuList>
                </MobileOnlyRightSection>
              </MobileMenuOverlay>
            </NavbarCenter>

            <NavbarRight>
              <RightMenuList>
                <LocaleMenuListItem
                  data-dropdown="true"
                  onMouseEnter={() => onMouseEnterDropdown('locale')}
                  onMouseLeave={onMouseLeaveDropdown}
                >
                  <LocaleButton
                    as="button"
                    aria-expanded={visibleDropdown === 'locale'}
                    onClick={() => onToggleDropdownVisibility('locale')}
                  >
                    {getLocaleLabel(router.locale)}
                    <KeyboardArrowDownIcon fontSize="small" />
                  </LocaleButton>
                  <LocaleDropdownContent
                    visible={visibleDropdown === 'locale'}
                    aria-expanded={visibleDropdown === 'locale'}
                  >
                    {LOCALE_OPTIONS.map(({ label, locale }) => (
                      <Link
                        key={locale}
                        href={router.asPath}
                        locale={locale}
                        passHref
                      >
                        <LocaleOption selected={router.locale === locale}>
                          {label}
                        </LocaleOption>
                      </Link>
                    ))}
                  </LocaleDropdownContent>
                </LocaleMenuListItem>

                {!isLoggedIn && !loading && (
                  <MenuListItemRight>
                    <LoginLink href={`${CUSTOMER_APP_HOST}/account/login`}>
                      {t('common:navigation.signIn')}
                    </LoginLink>
                  </MenuListItemRight>
                )}
                {isLoggedIn && !loading && (
                  <MenuListItemRight
                    data-dropdown="true"
                    onMouseEnter={() => onMouseEnterDropdown('account')}
                    onMouseLeave={onMouseLeaveDropdown}
                  >
                    <MenuListItemLink
                      as="button"
                      aria-expanded={visibleDropdown === 'account'}
                      onClick={() => onToggleDropdownVisibility('account')}
                    >
                      {currentUserAccount.firstName}{' '}
                      {currentUserAccount.lastName}
                      <Box display="flex" alignItems="center" ml={1}>
                        <AccountCircle />
                      </Box>
                    </MenuListItemLink>
                    <DropdownContent
                      visible={visibleDropdown === 'account'}
                      aria-expanded={visibleDropdown === 'account'}
                      className="navigation-dropdown"
                    >
                      <DropdownMenu>
                        {userMenuLinks.map(({ href, label }) => (
                          <Link key={href} href={href} passHref>
                            <DropdownMenuLink>
                              <span>{label}</span>
                              <ArrowForwardIcon fontSize="small" />
                            </DropdownMenuLink>
                          </Link>
                        ))}
                        <DropdownMenuLink onClick={logout} as="button">
                          <span>
                            <strong>{t('common:navigation.signOut')}</strong>
                          </span>
                          <ArrowForwardIcon fontSize="small" />
                        </DropdownMenuLink>
                      </DropdownMenu>
                    </DropdownContent>
                  </MenuListItemRight>
                )}
                <MenuListItemRight wrapsButton>
                  {typeof renderCTA === 'function' ? (
                    renderCTA()
                  ) : (
                    <Link href={buildFunnelUrl({ router })} passHref>
                      <CTAButton
                        component="a"
                        rel="noopener"
                        id="navbar-find-caterers"
                      >
                        {t('common:navigation.requestNow')}
                      </CTAButton>
                    </Link>
                  )}
                </MenuListItemRight>
              </RightMenuList>

              <Hidden mdUp>
                <HamburgerButton
                  aria-label={
                    mobileMenuOpen
                      ? 'Hide Navigation Menu'
                      : 'Show Navigation Menu'
                  }
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  <MenuListItemSecondary>
                    {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                  </MenuListItemSecondary>
                </HamburgerButton>
              </Hidden>
            </NavbarRight>
          </NavbarWrapper>
        </Container>
      </Navbar>
    </NavWrapper>
  );
}

const NavWrapper = styled.div`
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  width: 100%;
`;

const Navbar = styled.header`
  height: ${(props) => props.theme.navbar.height};
  background: white;
  z-index: ${(props) => props.theme.zIndex.appBar};
`;

const NavbarWrapper = styled.div`
  display: flex;
  align-items: center;
  height: ${(props) => props.theme.navbar.height};
`;

const NavbarLeft = styled.div`
  flex-shrink: 0;
`;

const NavbarCenter = styled.nav`
  flex: 1;
  display: flex;
  justify-content: center;

  ${(props) => props.theme.breakpoints.down('sm')} {
    display: block;
  }
`;

const NavbarRight = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const NavbarLogo = styled.a`
  width: 150px;
  height: ${(props) => props.theme.navbar.height};
  font-weight: 500;
  display: flex;
  align-items: center;
  flex-shrink: 0;

  & > span {
    display: inline-flex;
    margin-left: 10px;
    font-size: 18px;
  }
`;

const MobileMenuOverlay = styled.div<{ open: boolean }>`
  display: contents;

  ${(props) => props.theme.breakpoints.down('sm')} {
    display: ${(props) => (props.open ? 'block' : 'none')};
    position: fixed;
    top: var(${cssVars.topBarHeight.name});
    left: 0;
    right: 0;
    bottom: 0;
    z-index: ${(props) => props.theme.zIndex.appBar};
    background: white;
    overflow-y: auto;
  }
`;

const MobileOnlyRightSection = styled.div`
  display: none;

  ${(props) => props.theme.breakpoints.down('sm')} {
    display: block;
    padding: 8px 0;
  }
`;

const MenuList = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding-left: 0;
  align-items: center;

  ${(props) => props.theme.breakpoints.down('sm')} {
    display: block;
    flex-grow: unset;
    width: 100%;
  }
`;

const RightMenuList = styled(MenuList)`
  flex-shrink: 0;

  ${(props) => props.theme.breakpoints.down('sm')} {
    display: none;
  }
`;

const MenuListItem = styled.li`
  position: relative;
  min-height: ${(props) => props.theme.navbar.height};
  display: flex;
  align-items: center;

  ${(props) => props.theme.breakpoints.down('sm')} {
    display: block;
    min-height: auto;
  }

  ${(props) => props.theme.breakpoints.up('md')} {
    &[data-dropdown='true'] > .navigation-dropdown {
      display: block;
      pointer-events: none;
    }

    &[data-dropdown='true']:hover > .navigation-dropdown {
      pointer-events: auto;
    }
  }
`;

const MenuListItemRight = styled(MenuListItem)<{ wrapsButton?: boolean }>`
  min-height: auto;

  ${(props) => props.theme.breakpoints.down('sm')} {
    border-bottom: none;
    margin: ${(props) => (props.wrapsButton ? '8px 8px 0' : 'inherit')};

    > a {
      font-weight: ${(props) => (props.wrapsButton ? 600 : 400)};
      display: ${(props) => (props.wrapsButton ? 'flex' : 'inherit')};
      justify-content: ${(props) => (props.wrapsButton ? 'center' : 'initial')};
    }
  }
`;

const MenuListItemSecondary = styled(MenuListItem)`
  position: relative;
  min-height: ${(props) => props.theme.navbar.height};
  display: flex;
  align-items: center;
`;

const MenuListItemLink = styled.a`
  padding: 16px 16px;
  border: none;
  background: inherit;
  display: flex;
  width: 100%;
  margin-right: 0;
  flex-grow: 1;
  text-decoration: none;
  font-size: 15px;
  font-family: inherit;
  cursor: pointer;
  font-weight: 500;
  align-items: center;
  white-space: nowrap;
  a {
    text-decoration: none;
    color: inherit;
  }

  &,
  &:hover {
    color: ${(props) => props.theme.palette.text.primary};
  }

  ${(props) => props.theme.breakpoints.down('sm')} {
    font-weight: 700;
    justify-content: space-between;
  }
`;

const LoginLink = styled.a`
  padding: 16px 16px;
  text-decoration: none;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  color: ${(props) => props.theme.palette.text.primary};

  &:hover {
    color: ${(props) => props.theme.palette.text.secondary};
  }
`;

const DropdownContent = styled.div<{ visible?: boolean }>`
  background: white;
  min-height: 30px;
  overflow: hidden;
  position: absolute;
  top: ${(props) => props.theme.navbar.height};
  z-index: ${(props) => props.theme.zIndex.appBar};
  padding: 8px;
  left: 0;
  min-width: 260px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  display: none;
  opacity: 0;
  transform: translateY(8px);
  transition: opacity 0.25s ease, transform 0.25s ease;

  [data-dropdown='true']:hover > & {
    opacity: 1;
    transform: translateY(0);
  }

  ${(props) => props.theme.breakpoints.down('sm')} {
    position: static;
    background: transparent;
    padding: 0 30px;
    left: 0;
    width: 100%;
    border: none;
    border-radius: 0;
    box-shadow: none;
    border-top: 1px solid ${(props) => props.theme.palette.grey[200]};
    border-bottom: 1px solid ${(props) => props.theme.palette.grey[200]};
    background: rgba(0, 0, 0, 0.03);
    display: ${(props) => (props.visible ? 'block' : 'none')};
    opacity: 1;
    transform: none;
    transition: none;
  }
`;

const DropdownMenu = styled.div`
  padding: 4px;
`;

const DropdownMenuLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 12px;
  border: none;
  background: inherit;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.text.primary};
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  border-radius: 8px;
  width: 100%;
  text-align: left;
  font-family: inherit;

  &:hover {
    background: rgba(0, 0, 0, 0.04);

    svg {
      transform: rotate(-45deg);
    }
  }

  svg {
    color: ${({ theme }) => theme.palette.grey[600]};
    flex-shrink: 0;
    margin-left: 16px;
    transition: transform 0.25s ease;
  }

  ${(props) => props.theme.breakpoints.down('sm')} {
    font-size: 15px;
    padding: 14px 8px;
  }
`;

const CTAButton = styled(Button)`
  && {
    background-color: #f95542;
    color: white;
    border-radius: 100px;
    padding: 10px 24px;
    font-weight: 600;
    font-size: 15px;
    text-transform: none;
    white-space: nowrap;
    box-shadow: none;

    &:hover {
      background-color: #e04a39;
      box-shadow: none;
    }
  }
`;

const LocaleMenuListItem = styled.li`
  position: relative;
  min-height: ${(props) => props.theme.navbar.height};
  display: flex;
  align-items: center;
  list-style: none;
`;

const LocaleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 8px 12px;
  border: none;
  background: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  color: ${(props) => props.theme.palette.text.primary};
  white-space: nowrap;
`;

const LocaleDropdownContent = styled.div<{ visible?: boolean }>`
  background: white;
  position: absolute;
  top: ${(props) => props.theme.navbar.height};
  z-index: ${(props) => props.theme.zIndex.appBar};
  padding: 8px;
  left: 0;
  min-width: 140px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  display: none;
  opacity: 0;
  transform: translateY(8px);
  transition: opacity 0.25s ease, transform 0.25s ease;

  ${(props) => props.theme.breakpoints.up('md')} {
    ${LocaleMenuListItem}:hover > &,
    &[aria-expanded='true'] {
      display: block;
      opacity: 1;
      transform: translateY(0);
    }
  }

  ${(props) => props.theme.breakpoints.down('sm')} {
    position: static;
    width: 100%;
    border-radius: 0;
    box-shadow: none;
    opacity: 1;
    transform: none;
    transition: none;
    display: ${(props) => (props.visible ? 'block' : 'none')};
  }
`;

const LocaleOption = styled.a<{ selected?: boolean }>`
  display: block;
  padding: 8px 12px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => props.theme.palette.text.primary};
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: ${(props) => props.theme.palette.grey[100]};
  }

  ${(props) =>
    props.selected &&
    css`
      font-weight: 700;
    `}
`;

const HamburgerButton = styled(ButtonBase)`
  padding: 0px 20px;
  margin-left: auto;
  left: 16px;
`;

const NavbarEyebrow = styled.section`
  padding: 4px;
  background-color: ${(props) => props.theme.palette.grey[800]};
  color: white;
  font-size: 12px;
  z-index: ${(props) => props.theme.zIndex.appBar + 1};
`;

const NavbarEyebrowFlexWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;
  justify-content: space-between;

  ${({ theme }) => theme.breakpoints.down('xs')} {
    justify-content: center;
  }
`;

const TrustedShopsWrapper = styled.div`
  gap: 6px;
  display: flex;
  align-items: center;
  text-decoration: none;
  color: white;
`;

const Stars = styled(Box)`
  height: 17px;
  width: 100%;
  min-width: 70px;
`;

const RatingText = styled.span`
  ${({ theme }) => theme.breakpoints.down('xs')} {
    display: none;
  }
`;

const RatingCount = styled.span`
  flex-shrink: 0;
  ${({ theme }) => theme.breakpoints.down('md')} {
    display: none;
  }
`;

const Rating = styled.span`
  flex-shrink: 0;

  ${({ theme }) => theme.breakpoints.down('xs')} {
    display: none;
  }
`;

const TSLogoBox = styled(Box)`
  min-height: 12px;
`;

const PhoneLink = styled.a`
  color: inherit;
  text-decoration: inherit;
`;

export default Navigation;
